import { Injectable, Signal, computed, inject, signal } from '@angular/core';

import { KeycloakAuthService } from '@the-hive-frontend/keycloak-auth';

import { mapSessionUser } from './claim-mapping';
import { permissionsFor } from './permissions';
import {
  AuthSession,
  AuthStatus,
  Permission,
  SessionUser,
  SignInOptions,
} from './session.types';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthSession implements AuthSession {
  private readonly keycloak = inject(KeycloakAuthService);
  private readonly ready = signal(false);

  readonly requestedSignIn = signal<SignInOptions | null>(null);

  readonly status: Signal<AuthStatus> = computed(() => {
    if (!this.ready()) {
      return 'unknown';
    }
    return this.keycloak.isAuthenticated() ? 'authenticated' : 'anonymous';
  });

  readonly user: Signal<SessionUser | null> = computed(() => {
    if (!this.keycloak.isAuthenticated()) {
      return null;
    }
    return mapSessionUser(this.keycloak.tokenParsed ?? null);
  });

  readonly permissions: Signal<ReadonlySet<Permission>> = computed(() =>
    permissionsFor(this.user())
  );

  readonly claims = computed<Readonly<Record<string, unknown>> | null>(() => {
    if (!this.keycloak.isAuthenticated()) {
      return null;
    }
    return this.keycloak.tokenParsed ?? null;
  });

  async initialise(): Promise<void> {
    try {
      await this.keycloak.init();
    } finally {
      this.ready.set(true);
    }
  }

  async signIn(options?: SignInOptions): Promise<void> {
    this.requestedSignIn.set(options ?? null);
    await this.keycloak.login();
  }

  async signOut(): Promise<void> {
    await this.keycloak.logout();
  }

  async getAccessToken(): Promise<string | null> {
    const token = await this.keycloak.getToken();
    return token.length > 0 ? token : null;
  }

  async forceRefresh(): Promise<string | null> {
    const token = await this.keycloak.forceRefresh();
    return token.length > 0 ? token : null;
  }

  takeSignInError(): string | null {
    return this.keycloak.takeSignInError();
  }
}
